import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

function rel(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Small "last successful sync · retry now" line for CC-linked cards.
export const CcSyncLine = ({ sync, opportunityId, onRetried, className = "" }) => {
  const [busy, setBusy] = useState(false);
  if (!opportunityId) return null;
  const s = sync || { last_delivered_at: null, undelivered: 0, total: 0 };
  const pending = s.undelivered || 0;
  const last = rel(s.last_delivered_at);

  const retry = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/cc-sync/${encodeURIComponent(opportunityId)}/retry`);
      if (!data.webhook_configured) {
        toast.info("No CC webhook configured on PSDoc — nothing to push. Command Centre still pulls via polling.");
      } else if (data.retried === 0) {
        toast.success("Already in sync with Command Centre.");
      } else {
        toast.success(`Re-pushed ${data.delivered}/${data.retried} event(s) to Command Centre.`);
      }
      onRetried && onRetried();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`cc-sync-${opportunityId}`}
      onClick={(e) => e.stopPropagation()}
      className={`flex items-center gap-1.5 font-mono-jb text-[10px] ${className}`}
    >
      {pending > 0 ? (
        <>
          <AlertTriangle size={11} className="text-[#EAE04A]" />
          <span className="text-[#EAE04A]" data-testid={`cc-sync-status-${opportunityId}`}>
            {pending} pending{last ? ` · last ok ${last}` : ""}
          </span>
        </>
      ) : last ? (
        <>
          <CheckCircle2 size={11} className="text-[#00FF41]" />
          <span className="text-[#00FF41]" data-testid={`cc-sync-status-${opportunityId}`}>
            Synced {last}
          </span>
        </>
      ) : (
        <>
          <span className="inline-block w-2 h-2 rounded-full bg-[#8B949E]" />
          <span className="text-[#8B949E]" data-testid={`cc-sync-status-${opportunityId}`}>Not synced yet</span>
        </>
      )}
      <button
        type="button"
        data-testid={`cc-sync-retry-${opportunityId}`}
        onClick={retry}
        disabled={busy}
        className="ml-1 inline-flex items-center gap-1 px-1 py-0.5 -my-0.5 text-[#00F0FF] hover:underline disabled:opacity-50"
      >
        {busy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Retry now
      </button>
    </div>
  );
};

export default CcSyncLine;
